import React from "react";
import { Link } from "react-router-dom";

import CopyableIdentifier from "./CopyableIdentifier";

export type LotTraceType = "manufactured" | "supplier";

type InventoryLotOrigin = {
  trace_type?: unknown;
  receiving_invoice_id?: unknown;
  receiving_item_id?: unknown;
};

type TraceableLotNumberProps = {
  lotNumber: string | number | null | undefined;
  traceType: LotTraceType;
  className?: string;
  valueClassName?: string;
};

export function buildLotTracePath(
  lotNumber: string | number,
  traceType: LotTraceType,
) {
  const searchParams = new URLSearchParams({
    lookup: String(lotNumber),
    type: traceType,
  });

  return `/traceability?${searchParams.toString()}`;
}

/** Resolve traceability from the inventory record's origin, not its current screen. */
export function getInventoryLotTraceType(
  item: InventoryLotOrigin | null | undefined,
): LotTraceType {
  if (item?.trace_type === "supplier" || item?.trace_type === "manufactured") {
    return item.trace_type;
  }

  const isReceivingItem =
    Number(item?.receiving_invoice_id) > 0 || Number(item?.receiving_item_id) > 0;

  return isReceivingItem ? "supplier" : "manufactured";
}

/** A lot number that keeps its copy action and opens the matching trace report. */
export default function TraceableLotNumber({
  lotNumber,
  traceType,
  className,
  valueClassName,
}: TraceableLotNumberProps) {
  const value = lotNumber == null ? "" : String(lotNumber);
  const hasValue = value.trim().length > 0;
  const traceDescription =
    traceType === "supplier" ? "supplier item" : "manufactured product";

  return (
    <CopyableIdentifier
      value={lotNumber}
      description="lot number"
      className={className}
      valueClassName={valueClassName}
      displayValue={
        hasValue ? (
          <Link
            to={buildLotTracePath(value, traceType)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-inherit underline decoration-slate-400 underline-offset-2 transition-colors hover:text-slate-700 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500"
            title={`Trace ${traceDescription} lot ${value}`}
            onClick={(event) => event.stopPropagation()}
          >
            {value}
          </Link>
        ) : undefined
      }
    />
  );
}
